/* Limits card: pure model. The three limits, local validation, the merge patch and the API's per-field messages. */
import { PROJECT_USAGE_LIMITS_MAX, type Project, type ProjectUsageLimitsPatch } from '@hangar/shared';

export type LimitKey = keyof typeof PROJECT_USAGE_LIMITS_MAX;
export type LimitValues = Record<LimitKey, string>;
type Saved = Project['usageLimits'];

export const LIMITS: Array<{ key: LimitKey; label: string; hint: string; enforced: boolean; testId: string }> = [
  { key: 'maxConcurrentBuilds', label: 'Concurrent builds', hint: 'Builds of this project running at once on the build service; more wait in the queue', enforced: true, testId: 'limit-concurrent' },
  { key: 'maxBuildsPerDay', label: 'Builds per day', hint: 'Quota guardrail for one UTC day', enforced: false, testId: 'limit-per-day' },
  { key: 'maxQueuedBuilds', label: 'Queued builds', hint: 'Waiting builds before new ones are refused', enforced: false, testId: 'limit-queued' },
];

/** The limits the card shows: only what the scheduler enforces (builds per day and queued builds are stored, not applied). */
export const VISIBLE_LIMITS = LIMITS.filter((l) => l.enforced);

const asText = (n: number | null | undefined): string => (n === null || n === undefined ? '' : String(n));

/** The saved limits as the inputs hold them: empty text means no limit. */
export function initialLimitValues(saved: Saved): LimitValues {
  return { maxConcurrentBuilds: asText(saved?.maxConcurrentBuilds), maxBuildsPerDay: asText(saved?.maxBuildsPerDay), maxQueuedBuilds: asText(saved?.maxQueuedBuilds) };
}

/** Only digits can be typed. */
export const sanitizeLimit = (raw: string): string => raw.replace(/[^\d]/g, '');

function limitError(key: LimitKey, raw: string): string | undefined {
  const v = raw.trim();
  const max = PROJECT_USAGE_LIMITS_MAX[key];
  const invalid = !/^\d+$/.test(v) || Number(v) < 1 || Number(v) > max;
  return v && invalid ? `A whole number from 1 to ${max.toLocaleString('en-US')}, or empty for no limit` : undefined;
}

export function localLimitErrors(values: LimitValues): Record<LimitKey, string | undefined> {
  return Object.fromEntries(LIMITS.map(({ key }) => [key, limitError(key, values[key])])) as Record<LimitKey, string | undefined>;
}

export const hasLimitError = (errors: Record<string, string | undefined>): boolean => LIMITS.some(({ key }) => errors[key]);

interface ErrorContext {
  server: Record<string, string>;
  local: Record<LimitKey, string | undefined>;
  submitted: boolean;
  values: LimitValues;
}

/** A server message wins; a local rule shows after submit, or at once for a field that already has text. */
export function limitErrorFor(k: LimitKey, { server, local, submitted, values }: ErrorContext): string | undefined {
  return server[k] || (submitted ? local[k] : undefined) || (values[k] && local[k]) || undefined;
}

/** Merge patch (`null` clears): only the limits that differ from the saved ones. */
export function limitPatch(values: LimitValues, saved: Saved): ProjectUsageLimitsPatch {
  const patch: ProjectUsageLimitsPatch = {};
  for (const { key } of LIMITS) {
    const next = values[key].trim() ? Number(values[key]) : null;
    if (next !== (saved?.[key] ?? null)) patch[key] = next;
  }
  return patch;
}

export const limitsDirty = (patch: ProjectUsageLimitsPatch): boolean => Object.keys(patch).length > 0;

/** The toast after saving: what the concurrency limit now is. */
export function limitsSavedDescription(project: { name: string; usageLimits?: Saved }): string {
  const c = project.usageLimits?.maxConcurrentBuilds;
  return c ? `Up to ${c} ${c === 1 ? 'build' : 'builds'} of ${project.name} run at once.` : `No concurrency limit for ${project.name}.`;
}

/** `details[].path` ends in the limit's key (`usageLimits.maxConcurrentBuilds`): its message goes to that field. */
export function limitFieldsFromDetails(details: unknown): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const issue of Array.isArray(details) ? details : []) {
    const path = (issue as { path?: unknown }).path;
    const key = Array.isArray(path) ? path[path.length - 1] : undefined;
    if (typeof key === 'string' && key in PROJECT_USAGE_LIMITS_MAX) fields[key] = String((issue as { message?: unknown }).message ?? 'invalid');
  }
  return fields;
}
